import React, { useEffect, useState, useRef } from "react";
import { Toast } from "primereact/toast";
import { confirmDialog } from "primereact/confirmdialog";
import { deletecertification } from "../Services/DeleteService";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { AutoComplete } from "primereact/autocomplete";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import useAccessToken from "../Custom/useAccessToken";
import { getCertificationsDetails, getSkills } from "../Services/GetService";
import { Skeleton } from "primereact/skeleton";
import { useUserContext } from "../Context/UserContext";
import { saveCertification } from "../Services/UpdateService";
import { saveSkill } from "../Services/SkillService";
import { formatDateUsingMoment } from "../Utils/Year";

interface SkillItem {
  skillId: string;
  skillName: string;
}

interface CertItem {
  certificationId: string;
  certificationName: string;
  issuingOrganization: string;
  expirationDate: string | null;
  credentialId: string;
  credentialLink: string;
  certificationDetail: string;
  skillsList: SkillItem[];
  isHavingExpiry: boolean;
  issueDate: string;
}

const months = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];
const years = Array.from({ length: 50 }, (_, i) => ({
  label: `${2025 - i}`,
  value: String(2025 - i),
}));

interface ProfileCardDashboardProps {
  profileID?: string | null;
  isOwnProfile?: boolean;
}

const LicensesCertifications: React.FC<ProfileCardDashboardProps> = ({
  profileID,
  isOwnProfile,
}) => {
  const [skillsOptions, setSkillsOptions] = useState<SkillItem[]>([]);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [certifications, setCertifications] = useState<CertItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const toast = useRef<any>(null);
  const handleDeleteCertification = async (certificationId?: string) => {
    const idToDelete = certificationId || deleteId;
    if (!accessToken || !idToDelete) return;
    try {
      await deletecertification(accessToken, idToDelete);
      setCertifications((prev) =>
        prev.filter((cert) => cert.certificationId !== idToDelete)
      );
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Certification deleted successfully",
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete certification",
        life: 3000,
      });
      console.error("Failed to delete certification", error);
    } finally {
      setDeleteId(null);
    }
  };
  const { accessToken } = useAccessToken();
  const { userId } = useUserContext();

  const customHeader = (
    <div className="flex align-items-center gap-2 px-2">
      <span className="font-medium text-900 text-lg">
        {editMode ? "Edit Certification" : "Add Certification"}
      </span>
    </div>
  );

  const [form, setForm] = useState({
    certificationId: null as string | null,
    certificationName: "",
    issuingOrganization: "",
    issueMonth: null as string | null,
    issueYear: null as string | null,
    expirationDate: null as Date | null,
    expMonth: null as string | null,
    expYear: null as string | null,
    credentialId: "",
    credentialLink: "",
    isHavingExpiry: true,
    skillsList: [] as string[],
  });

  const [selectedSkills, setSelectedSkills] = useState<SkillItem[]>([]);
  const [skillInput, setSkillInput] = useState("");
  const [filteredSkills, setFilteredSkills] = useState<SkillItem[]>([]);
  const [dateErrors, setDateErrors] = useState({
    issueDate: "",
    expirationDate: "",
  });

  // Fetch skills for MultiSelect
  useEffect(() => {
    const fetchSkills = async () => {
      if (accessToken) {
        try {
          const skills = await getSkills(accessToken);
          setSkillsOptions(skills);
        } catch {
          setSkillsOptions([]);
        }
      } else {
        setSkillsOptions([]);
      }
    };
    fetchSkills();
  }, [accessToken]);

  // Fetch certifications
  useEffect(() => {
    const fetchCerts = async () => {
      if (!accessToken || !profileID) {
        setCertifications([]);
        return;
      }

      setLoading(true);
      try {
        const certifications: CertItem[] = await getCertificationsDetails(
          accessToken,
          profileID
        );
        setCertifications(certifications);
      } catch (err) {
        console.error("Failed to fetch certifications", err);
        setCertifications([]);
      }
      setLoading(false);
    };
    fetchCerts();
  }, [accessToken, profileID]);

  // Helper functions for skills autocomplete
  const searchSkills = (event: { query: string }) => {
    const filtered = skillsOptions.filter((skill) =>
      skill.skillName.toLowerCase().includes(event.query.toLowerCase())
    );
    setFilteredSkills(filtered);
  };

  const handleSkillSelect = async (e: { value: string }) => {
    await addSkill(e.value);
  };

  const handleSkillKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const overlay = document.querySelector(".p-autocomplete-panel");
  
      const highlighted = overlay?.querySelector(".p-highlight");
  
      if (highlighted) {
        return;
      }
  
      if (skillInput.trim()) {
        e.preventDefault();
        await addSkill(skillInput.trim());
      }
    }
  };
  

  const emptyFormData = () => {
    setSidebarVisible(false);
    setEditMode(false);
    setForm({
      certificationId: null,
      certificationName: "",
      issuingOrganization: "",
      issueMonth: null,
      issueYear: null,
      expirationDate: null,
      expMonth: null,
      expYear: null,
      credentialId: "",
      credentialLink: "",
      isHavingExpiry: true,
      skillsList: [],
    });
    setSelectedSkills([]);
    setSkillInput("");
    setDateErrors({
      issueDate: "",
      expirationDate: "",
    });
  };

  const addSkill = async (skillName: string) => {
    if (!skillName) return;

    // Check if it's an existing skill
    const existingSkill = skillsOptions.find(
      (skill) => skill.skillName.toLowerCase() === skillName.toLowerCase()
    );

    if (existingSkill) {
      // Check if skill is already selected
      if (
        selectedSkills.find((skill) => skill.skillId === existingSkill.skillId)
      ) {
        toast.current?.show({
          severity: "info",
          summary: "Already Added",
          detail: `Skill "${skillName}" is already selected`,
          life: 2000,
        });
        setSkillInput("");
        return;
      }

      // Add existing skill
      const newSelectedSkills = [...selectedSkills, existingSkill];
      setSelectedSkills(newSelectedSkills);
      handleChange(
        "skillsList",
        newSelectedSkills.map((skill) => skill.skillId)
      );
    } else {
      // Check if we're trying to add a skill that matches an already selected skill by name
      if (
        selectedSkills.find(
          (skill) => skill.skillName.toLowerCase() === skillName.toLowerCase()
        )
      ) {
        toast.current?.show({
          severity: "info",
          summary: "Already Added",
          detail: `Skill "${skillName}" is already selected`,
          life: 2000,
        });
        setSkillInput("");
        return;
      }

      // Create new skill
      try {
        if (accessToken) {
          const newSkillData = {
            skillId: null,
            skillName: skillName,
          };

          const savedSkill = await saveSkill(accessToken, newSkillData);

          // The SaveSkill API returns the new skillId as a string (GUID)
          const newSkillId = savedSkill;

          if (
            !newSkillId ||
            typeof newSkillId !== "string" ||
            newSkillId.trim() === ""
          ) {
            console.error(
              "SaveSkill API returned invalid skillId:",
              savedSkill
            );
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: "Failed to create skill - API returned invalid response",
              life: 3000,
            });
            setSkillInput("");
            return;
          }

          // Add the new skill to the options and selected skills
          const newSkill: SkillItem = {
            skillId: newSkillId,
            skillName: skillName,
          };

          setSkillsOptions((prev) => [...prev, newSkill]);
          const newSelectedSkills = [...selectedSkills, newSkill];
          setSelectedSkills(newSelectedSkills);
          handleChange(
            "skillsList",
            newSelectedSkills.map((skill) => skill.skillId)
          );

          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: `New skill "${skillName}" created and added`,
            life: 3000,
          });
        }
      } catch (error) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to create new skill",
          life: 3000,
        });
        console.error("Failed to create new skill", error);
      }
    }

    setSkillInput("");
  };

  const removeSkill = (skillToRemove: SkillItem) => {
    const newSelectedSkills = selectedSkills.filter(
      (skill) => skill.skillId !== skillToRemove.skillId
    );
    setSelectedSkills(newSelectedSkills);
    handleChange(
      "skillsList",
      newSelectedSkills.map((skill) => skill.skillId)
    );
  };

  // Helper to open sidebar in edit mode with data
  const handleEditCert = (cert: CertItem) => {
    setEditMode(true);
    let expMonth: string | null = null;
    let expYear: string | null = null;
    if (cert.expirationDate) {
      const date = new Date(cert.expirationDate);
      expMonth = months[date.getMonth()].value;
      expYear = String(date.getFullYear());
    }
    let issueMonth: string | null = null;
    let issueYear: string | null = null;
    const anyCert: any = cert as any;
    const issueDateRaw =
      anyCert.issueDate || anyCert.IssueDate || anyCert.startDate || null;
    if (issueDateRaw) {
      const d = new Date(issueDateRaw);
      if (!isNaN(d.getTime())) {
        issueMonth = months[d.getMonth()].value;
        issueYear = String(d.getFullYear());
      }
    }

    setForm({
      certificationId: cert.certificationId,
      certificationName: cert.certificationName || "",
      issuingOrganization: cert.issuingOrganization || "",
      issueMonth: issueMonth,
      issueYear: issueYear,
      expirationDate: cert.expirationDate
        ? new Date(cert.expirationDate)
        : null,
      expMonth: expMonth ? String(expMonth) : null, // ensure string
      expYear,
      credentialId: cert.credentialId || "",
      credentialLink: cert.credentialLink || "",
      isHavingExpiry: cert.isHavingExpiry,
      skillsList: (cert.skillsList || []).map((s) => s.skillId),
    });

    // Set selected skills for autocomplete
    setSelectedSkills(cert.skillsList || []);

    // Clear date errors when editing
    setDateErrors({
      issueDate: "",
      expirationDate: "",
    });
    setSidebarVisible(true);
  };
  // URL validation helper — use URL constructor for robustness
  const isValidURL = (string: string): boolean => {
    if (!string.trim()) return true;
    try {
      const url = new URL(string);
      return (
        (url.protocol === "http:" || url.protocol === "https:") &&
        url.hostname.length > 0 &&
        url.hostname.includes(".")
      );
    } catch {
      try {
        const url = new URL(`https://${string}`);
        return url.hostname.length > 0 && url.hostname.includes(".");
      } catch {
        return false;
      }
    }
  };

  // Date validation helper functions
  const validateDates = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11

    const errors = {
      issueDate: "",
      expirationDate: "",
    };

    if ((form.issueMonth && !form.issueYear) || (!form.issueMonth && form.issueYear)) {
      errors.issueDate = "Please select both the issue month and year, or leave both fields blank";
    }

    // Validate issue date
    if (form.issueMonth && form.issueYear) {
      const issueDate = new Date(
        parseInt(form.issueYear),
        parseInt(form.issueMonth) - 1,
        1
      );
      const currentDateFirstDay = new Date(currentYear, currentMonth - 1, 1);

      if (issueDate > currentDateFirstDay) {
        errors.issueDate = "Issue date cannot be in the future";
      }
    }

    if (((form.expMonth && !form.expYear) || (!form.expMonth && form.expYear))) {
      errors.expirationDate = "Please select both the expiration month and year, or leave both fields blank";
    }

    // Validate expiration date (only if certification expires)
    if (form.expirationDate !== null && form.expMonth && form.expYear) {
      const expirationDate = new Date(
        parseInt(form.expYear),
        parseInt(form.expMonth) - 1,
        1
      );

      // Check if expiration date is before issue date
      if (form.issueMonth && form.issueYear) {
        const issueDate = new Date(
          parseInt(form.issueYear),
          parseInt(form.issueMonth) - 1,
          1
        );
        if (expirationDate < issueDate) {
          errors.expirationDate = "Expiration date cannot be before issue date";
        }
      }
    }

    if (form.expirationDate == null && form.expMonth && form.expYear) {
      const expirationDate = new Date(
        parseInt(form.expYear),
        parseInt(form.expMonth) - 1,
        1
      );

      // Check if expiration date is before issue date
      if (form.issueMonth && form.issueYear) {
        const issueDate = new Date(
          parseInt(form.issueYear),
          parseInt(form.issueMonth) - 1,
          1
        );
        if (expirationDate < issueDate) {
          if (form.isHavingExpiry === true)
            errors.expirationDate =
              "Expiration date cannot be before issue date";
        }
      }
    }

    setDateErrors(errors);

    if (form.isHavingExpiry === true)
      return !errors.issueDate && !errors.expirationDate;
    else return true;
  };

  // Add or update certification
  const handleSubmitCertification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken) return;

    // Validation for required fields
    if (
      !form.certificationName.trim() ||
      !form.issuingOrganization.trim() 
      // !form.issueMonth ||  !form.issueYear ||
      // (form.expirationDate !== null && (!form.expMonth || !form.expYear))
    ) {
      toast.current?.show({
        severity: "error",
        summary: "Missing required fields.",
        detail: "Please complete all required fields.",
        life: 4000,
      });
      return;
    }

    // URL validation
    if (form.credentialLink && !isValidURL(form.credentialLink)) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please enter a valid URL (e.g., https://example.com).",
        life: 4000,
      });
      return;
    }

    // Date validation
    if (!validateDates()) {
      toast.current?.show({
        severity: "error",
        summary: "Date Validation Error",
        detail: "Please check the date fields for errors",
        life: 4000,
      });
      return;
    }

    // Prepare payload (Education structure) - include IssueDate/ExpirationDate as ISO strings
    const payload: any = {
      CertificationId: form.certificationId ?? null,
      CertificationName: form.certificationName,
      IssuingOrganization: form.issuingOrganization,
      CredentialId: form.credentialId,
      CredentialLink: form.credentialLink,
      CertificationDetail: "NA", // Add field if needed
      IsHavingExpiry: form.isHavingExpiry,
      // Backwards-compatible month/year fields (some APIs expect these)
      StartMonth: form.issueMonth ? form.issueMonth : null,
      StartYear: form.issueYear ? form.issueYear : null,
      EndMonth: form.expMonth ? form.expMonth : null,
      EndYear: form.expYear ? form.expYear : null,
      // Preferred fields: full ISO date strings (YYYY-MM-DD)
      IssueDate:
        form.issueYear && form.issueMonth
          ? `${form.issueYear}-${String(form.issueMonth).padStart(2, "0")}-01`
          : null,
      // Prefer constructing expiration date from selected month/year when available.
      ExpirationDate:
        form.expYear && form.expMonth
          ? `${String(form.expYear)}-${String(form.expMonth).padStart(
              2,
              "0"
            )}-01`
          : form.expirationDate
          ? form.expirationDate.toISOString().split("T")[0]
          : null,
      SkillsList: Array.isArray(form.skillsList)
        ? form.skillsList
            .map((s: any) => (typeof s === "string" ? s : s?.skillId))
            .filter((id: any) => typeof id === "string" && id.length > 0)
        : [],
    };
    if (userId) {
      payload.ProfileId = userId;
    }

    try {
      await saveCertification(accessToken, payload);
      const updatedCerts = await getCertificationsDetails(accessToken);
      setCertifications(updatedCerts);
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: editMode
          ? "Certification updated successfully"
          : "Certification added successfully",
        life: 3000,
      });
      setSidebarVisible(false);
      setEditMode(false);
      setForm({
        certificationId: null,
        certificationName: "",
        issuingOrganization: "",
        issueMonth: null,
        issueYear: null,
        expirationDate: null,
        expMonth: null,
        expYear: null,
        credentialId: "",
        credentialLink: "",
        isHavingExpiry: true,
        skillsList: [],
      });
      setSelectedSkills([]);
      setSkillInput("");
      // Clear date errors when form is reset
      setDateErrors({
        issueDate: "",
        expirationDate: "",
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to ${editMode ? "update" : "add"} certification`,
        life: 3000,
      });
      console.error(
        `Failed to ${editMode ? "update" : "add"} certification`,
        error
      );
      if (error?.response) {
        console.error("API error response:", error.response);
      }
    }
  };

  const handleChange = (
    field: string,
    value: string | number | boolean | string[] | Date | null
  ) => {
    if (field === "issueMonth" || field === "expMonth") {
      setForm((prev) => ({ ...prev, [field]: value ? String(value) : null }));
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }

    // Trigger date validation when date fields change
    if (
      [
        "issueMonth",
        "issueYear",
        "expMonth",
        "expYear",
        "expirationDate",
        "isHavingExpiry",
      ].includes(field)
    ) {
      // Use setTimeout to ensure state is updated before validation
      setTimeout(() => {
        const updatedForm = {
          ...form,
          [field]:
            field === "issueMonth" || field === "expMonth"
              ? value
                ? String(value)
                : null
              : value,
        };
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;

        const errors = {
          issueDate: "",
          expirationDate: "",
        };

        if ((updatedForm.issueMonth && !updatedForm.issueYear) || (!updatedForm.issueMonth && updatedForm.issueYear)) {
          errors.issueDate = "Please select both the issue month and year, or leave both fields blank";
        }

        // Validate issue date
        if (updatedForm.issueMonth && updatedForm.issueYear) {
          const issueDate = new Date(
            parseInt(updatedForm.issueYear),
            parseInt(updatedForm.issueMonth) - 1,
            1
          );
          const currentDateFirstDay = new Date(
            currentYear,
            currentMonth - 1,
            1
          );

          if (issueDate > currentDateFirstDay) {
            errors.issueDate = "Issue date cannot be in the future";
          }
        }

        if (((updatedForm.expMonth && !updatedForm.expYear) || (!updatedForm.expMonth && updatedForm.expYear))) {
          errors.expirationDate = "Please select both the expiration month and year, or leave both fields blank";
        }

        // Validate expiration date (only if certification expires)
        if (
          updatedForm.expirationDate !== null &&
          updatedForm.expMonth &&
          updatedForm.expYear
        ) {
          const expirationDate = new Date(
            parseInt(updatedForm.expYear),
            parseInt(updatedForm.expMonth) - 1,
            1
          );

          // Check if expiration date is before issue date
          if (updatedForm.issueMonth && updatedForm.issueYear) {
            const issueDate = new Date(
              parseInt(updatedForm.issueYear),
              parseInt(updatedForm.issueMonth) - 1,
              1
            );
            if (expirationDate < issueDate) {
              if (updatedForm.isHavingExpiry === true)
                errors.expirationDate =
                  "Expiration date cannot be before issue date";
            }
          }
        }

        if (
          updatedForm.expirationDate == null &&
          updatedForm.expMonth &&
          updatedForm.expYear
        ) {
          const expirationDate = new Date(
            parseInt(updatedForm.expYear),
            parseInt(updatedForm.expMonth) - 1,
            1
          );

          // Check if expiration date is before issue date
          if (updatedForm.issueMonth && updatedForm.issueYear) {
            const issueDate = new Date(
              parseInt(updatedForm.issueYear),
              parseInt(updatedForm.issueMonth) - 1,
              1
            );
            if (expirationDate < issueDate) {
              if (updatedForm.isHavingExpiry === true)
                errors.expirationDate =
                  "Expiration date cannot be before issue date";
            }
          }
        }

        setDateErrors(errors);
      }, 0);
    }
  };

  return (
    <>
      <Toast ref={toast} baseZIndex={5000} />
      {(certifications?.length > 0 || isOwnProfile) && (
          <>
            <Card
              className="mb-3 shadow-1"
              title={
                <div className="flex align-items-center justify-content-between mb-4">
                  <span className="text-title font-semibold">
                    Licenses & Certifications
                  </span>
                  {isOwnProfile && (
                    <Button
                      icon="pi pi-plus"
                      label="Add certification"
                      size="small"
                      severity="info"
                      data-pr-tooltip="Add certification"
                      data-pr-position="right"
                      aria-label="Add Certification"
                      className="p-button p-component"
                      onClick={() => setSidebarVisible(true)}
                    />
                  )}
                </div>
              }
            >
              <div className="flex flex-column gap-4">
                {loading ? (
                  <div>
                    {["s1", "s2"].map((id) => (
                      <div
                        key={id}
                        className="p-3 flex justify-content-between gap-3 border-round surface-card mb-2"
                      >
                        <div className="flex flex-column">
                          <Skeleton width="320px" height="1.4rem" className="mb-2" />
                          <Skeleton width="180px" height="1rem" className="mb-2" />
                          <Skeleton width="220px" height="1rem" className="mb-2" />
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Skeleton width="90px" height="1.4rem" />
                            <Skeleton width="90px" height="1.4rem" />
                          </div>
                        </div>
                        <div>
                          <Skeleton width="120px" height="1rem" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  certifications.map((c: CertItem, index) => (
                    <React.Fragment key={c.certificationId}>
                      <div className="flex justify-content-between gap-3">
                        <div className="flex flex-column">
                          <span className="text-base font-semibold mb-2">
                            {c.certificationName}
                          </span>
                          <span className="text-sm font-medium mb-2">
                            {c.issuingOrganization}
                          </span>
                          <span className="text-sm profile-description mb-2">
                            {
                              c.issueDate ?
                              `Issued: ${formatDateUsingMoment(c.issueDate)}` : ""
                            }
                            {/* Issued: {formatDateUsingMoment(c.issueDate)} */}
                          </span>
                          {c.credentialLink ? (
                            <a
                              href={
                                c.credentialLink.startsWith("https")
                                  ? c.credentialLink
                                  : `https://${c.credentialLink}`
                              }
                              className="text-sm mb-2 text-primary hover:underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {c.credentialLink}
                            </a>
                          ) : null}
                          {c.credentialId && (
                            <span className="text-sm profile-description mb-2">
                              Credential ID: {c.credentialId}
                            </span>
                          )}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {c.skillsList.map((t) => (
                              <span
                                key={t.skillId}
                                className="px-2 py-1 text-sm font-medium border-round-3xl course-name-font"
                              >
                                {t.skillName}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex align-items-start gap-1">
                          {isOwnProfile && (
                            <>
                              <Button
                                icon="pi pi-pencil"
                                className="p-button-text p-button shadow-none"
                                tooltip="Edit certification"
                                aria-label="Edit Certification"
                                onClick={() => handleEditCert(c)}
                              />
                              <Button
                                icon="pi pi-trash"
                                className="p-button-text p-button-danger shadow-none"
                                tooltip="Delete certification"
                                aria-label="Delete Certification"
                                onClick={() => {
                                  confirmDialog({
                                    message:
                                      "Are you sure you want to delete this certification?",
                                    header: "Confirmation",
                                    icon: "pi pi-exclamation-triangle",
                                    defaultFocus: "reject",
                                    accept: () => {
                                      void handleDeleteCertification(
                                        c.certificationId
                                      );
                                    },
                                    reject: () => {
                                      toast.current?.show({
                                        severity: "warn",
                                        summary: "Cancelled",
                                        detail: "Delete cancelled",
                                        life: 3000,
                                      });
                                    },
                                  });
                                }}
                              />
                            </>
                          )}
                        </div>
                      </div>
                      {index < certifications.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                )}
              </div>
            </Card>
            <Sidebar
              header={customHeader}
              visible={sidebarVisible}
              position="right"
              onHide={() => {
                setSidebarVisible(false);
                setEditMode(false);
                setForm({
                  certificationId: null,
                  certificationName: "",
                  issuingOrganization: "",
                  issueMonth: null,
                  issueYear: null,
                  expirationDate: null,
                  expMonth: null,
                  expYear: null,
                  credentialId: "",
                  credentialLink: "",
                  isHavingExpiry: true,
                  skillsList: [],
                });
                setSelectedSkills([]);
                setSkillInput("");
                setDateErrors({
                  issueDate: "",
                  expirationDate: "",
                });
              }}
              style={{ width: "55vw" }}
              showCloseIcon
              blockScroll
              className="p-0"
            >
              <Divider />
              <form
                className="flex flex-column h-full p-0"
                onSubmit={handleSubmitCertification}
              >
                <div className="pt-3 px-1 flex-1 flex flex-column overflow-y-auto">
                  <div className="mb-4">
                    <label
                      htmlFor="cert-name"
                      className="block mb-2 text-900 text-base"
                    >
                      Certification Name <span style={{ color: "red" }}>*</span>
                    </label>
                    <InputText
                      id="cert-name"
                      value={form.certificationName}
                      onChange={(e) =>
                        handleChange("certificationName", e.target.value)
                      }
                      placeholder="e.g., Certified Public Accountant (CPA)"
                      className="w-full text-sm input-sm-height"
                    />
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="issuing-org"
                      className="block mb-2 text-900 text-base"
                    >
                      Issuing Organization <span style={{ color: "red" }}>*</span>
                    </label>
                    <InputText
                      id="issuing-org"
                      value={form.issuingOrganization}
                      onChange={(e) =>
                        handleChange("issuingOrganization", e.target.value)
                      }
                      placeholder="e.g., Tennessee State Board of Accountants"
                      className="w-full text-sm input-sm-height"
                    />
                  </div>
                  <div className="flex gap-3 mb-0">
                    <div style={{ flex: 1 }}>
                      <label
                        htmlFor="issue-month"
                        className="block mb-2 text-900 text-base"
                      >
                        Issue Month 
                        {/* <span style={{ color: "red" }}>*</span> */}
                      </label>
                      <Dropdown
                        inputId="issue-month"
                        value={form.issueMonth}
                        options={months}
                        onChange={(e) => handleChange("issueMonth", e.value)}
                        placeholder="Select month"
                        className={`w-full text-sm dropdown-placeholder-sm ${dateErrors.issueDate ? "p-invalid" : ""
                          }`}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label
                        htmlFor="issue-year"
                        className="block mb-2 text-900 text-base"
                      >
                        Issue Year 
                        {/* <span style={{ color: "red" }}>*</span> */}
                      </label>
                      <Dropdown
                        inputId="issue-year"
                        value={form.issueYear}
                        options={years}
                        onChange={(e) => handleChange("issueYear", e.value)}
                        placeholder="Select year"
                        className={`w-full text-sm dropdown-placeholder-sm ${dateErrors.issueDate ? "p-invalid" : ""
                          }`}
                      />
                    </div>
                  </div>
                  {dateErrors.issueDate && (
                    <div className="mb-0">
                      <small className="p-error">{dateErrors.issueDate}</small>
                    </div>
                  )}
                  <div className="mb-4 mt-4">
                    <Checkbox
                      inputId="expirationDate"
                      checked={!form.isHavingExpiry}
                      onChange={(e) => handleChange("isHavingExpiry", !e.checked)}
                    />
                    <label htmlFor="expirationDate" className="ml-2 text-base">
                      This certification does not expire.
                    </label>
                  </div>
                  <div className="flex gap-3 mb-0">
                    <div style={{ flex: 1 }}>
                      <label
                        htmlFor="exp-month"
                        className="block mb-2 text-900 text-base"
                      >
                        Expiration Month{" "}
                        {/* {form.expirationDate !== null && (
                          <span style={{ color: "red" }}>*</span>
                        )} */}
                      </label>
                      <Dropdown
                        inputId="exp-month"
                        value={form.expMonth}
                        options={months}
                        onChange={(e) => handleChange("expMonth", e.value)}
                        placeholder="Select month"
                        className={`w-full text-sm dropdown-placeholder-sm ${dateErrors.expirationDate ? "p-invalid" : ""
                          }`}
                        disabled={!form.isHavingExpiry}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label
                        htmlFor="exp-year"
                        className="block mb-2 text-900 text-base"
                      >
                        Expiration Year{" "}
                        {/* {form.expirationDate !== null && (
                          <span style={{ color: "red" }}>*</span>
                        )} */}
                      </label>
                      <Dropdown
                        inputId="exp-year"
                        value={form.expYear}
                        options={years}
                        onChange={(e) => handleChange("expYear", e.value)}
                        placeholder="Select year"
                        className={`w-full text-sm dropdown-placeholder-sm ${dateErrors.expirationDate ? "p-invalid" : ""
                          }`}
                        disabled={!form.isHavingExpiry}
                      />
                    </div>
                  </div>
                  {dateErrors.expirationDate && (
                    <div className="mb-0">
                      <small className="p-error">{dateErrors.expirationDate}</small>
                    </div>
                  )}
                  <div className="mb-4 mt-4">
                    <label
                      htmlFor="credential-id"
                      className="block mb-2 text-900 text-base"
                    >
                      Credential ID
                    </label>
                    <InputText
                      id="credential-id"
                      value={form.credentialId}
                      onChange={(e) => handleChange("credentialId", e.target.value)}
                      placeholder="e.g., TN-12345"
                      className="w-full text-sm input-sm-height"
                    />
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="cert-link"
                      className="block mb-2 text-900 text-base"
                    >
                      Certification Link
                    </label>
                    <InputText
                      id="cert-link"
                      value={form.credentialLink}
                      onChange={(e) => handleChange("credentialLink", e.target.value)}
                      placeholder="https://www.aicpa-cima.com/"
                      className={`w-full text-sm input-sm-height ${form.credentialLink && !isValidURL(form.credentialLink)
                          ? "p-invalid"
                          : ""
                        }`}
                    />
                    {form.credentialLink && !isValidURL(form.credentialLink) && (
                      <small className="p-error">
                        Please enter a valid URL (e.g., https://example.com)
                      </small>
                    )}
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="cert-skills"
                      className="block mb-2 text-900 text-base"
                    >
                      Skills
                    </label>
                    <div className="mb-2">
                      <AutoComplete
                        id="cert-skills"
                        value={skillInput}
                        suggestions={filteredSkills.map((skill) => skill.skillName)}
                        completeMethod={searchSkills}
                        onChange={(e) => setSkillInput(e.value)}
                        onSelect={handleSkillSelect}
                        onKeyDown={handleSkillKeyDown}
                        placeholder="Add relevant skills and press Enter"
                        className="w-full text-sm"
                        dropdown
                        forceSelection={false}
                      />
                      <small className="text-500">
                        Press Enter after typing each skill
                      </small>
                    </div>
                    {selectedSkills.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedSkills.map((skill) => (
                          <span
                            key={skill.skillId}
                            className="inline-flex align-items-center bg-primary-100 text-primary-800 px-2 py-1 border-round text-sm"
                          >
                            {skill.skillName}
                            <button
                              type="button"
                              onClick={() => removeSkill(skill)}
                              className="ml-2 text-primary-600 hover:text-primary-800 border-none bg-transparent cursor-pointer"
                              style={{ fontSize: "0.75rem" }}
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className=" flex gap-3 align-items-center mb-3">
                    <Button
                      label={editMode ? "Update certification" : "Add certification"}
                      className="p-button p-component p-button-sm"
                      icon={editMode ? "pi pi-pencil" : "pi pi-plus"}
                      type="submit"
                    />
                    <Button
                      label="Cancel"
                      className="p-button p-component p-button-outlined p-button-sm"
                      type="button"
                      onClick={() => emptyFormData()}
                    />
                  </div>
                </div>
              </form>
            </Sidebar>
          </>          
      )}
    </>
  );
};

export default LicensesCertifications;
